import { useEffect, useMemo, useReducer, useRef } from 'react';

import { PageInfo } from 'components/Navigation';

import { DataAccessFilters, initialViewState, viewReducer } from './utils';

/**
 * The tab's filters, open rows and pages. Another identity starts from the
 * defaults.
 */
export const useDataAccessView = (identity: string) => {
  const [stored, dispatch] = useReducer(
    viewReducer,
    identity,
    initialViewState,
  );
  // Resetting during render rather than in an effect means no request goes
  // out with the last identity's filters or pages.
  const state =
    stored.identity === identity ? stored : initialViewState(identity);
  if (state !== stored) dispatch({ type: 'reset', identity });

  const actions = useMemo(
    () => ({
      setFilters: (filters: Partial<DataAccessFilters>) =>
        dispatch({ type: 'setFilters', filters }),
      resetPages: () => dispatch({ type: 'resetPages' }),
      setDatabasePage: (page: PageInfo) =>
        dispatch({ type: 'setDatabasePage', page }),
      setSchemaPage: (databaseUuid: string, page: PageInfo) =>
        dispatch({ type: 'setSchemaPage', databaseUuid, page }),
      setTablePage: (schemaUuid: string, page: PageInfo) =>
        dispatch({ type: 'setTablePage', schemaUuid, page }),
      toggleDatabase: (uuid: string) =>
        dispatch({ type: 'toggleDatabase', uuid }),
      toggleSchema: (uuid: string) => dispatch({ type: 'toggleSchema', uuid }),
      toggleTable: (uuid: string) => dispatch({ type: 'toggleTable', uuid }),
    }),
    [],
  );

  return { state, actions };
};

export type DataAccessViewActions = ReturnType<
  typeof useDataAccessView
>['actions'];

/**
 * Calls `onChange` when the identity's rows are recomputed (`computedAt`
 * changes), so the lists can go back to page 1. A first value, or another
 * identity's, is not a change.
 */
export const useOnRollupChange = (
  identity: string,
  computedAt: string | null | undefined,
  onChange: () => void,
) => {
  const seen = useRef({ identity, computedAt });
  useEffect(() => {
    if (!computedAt) return;
    const last = seen.current;
    if (
      last.identity === identity &&
      last.computedAt &&
      last.computedAt !== computedAt
    )
      onChange();
    seen.current = { identity, computedAt };
  }, [identity, computedAt, onChange]);
};
